import { makeColumns } from "./columns"
import { TreeGridTranslations } from "../hooks/use-translations"
import { GanttZoom, Option, RootNode } from "../types"
import { makeColorKeysEnum } from "../utils"
import { makeCfgBase } from "../base/layout/make-cfg-base"
import { makeColumnLayout } from "../base/layout/make-column-layout"
import { ToolbarBuilder } from "../base/layout/make-toolbar"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"

export const makeLayout = (props: MakeLayoutProps) => {
  const { id, translations, showGantt, showGanttToolbarActions, showFilters, locale, root, calendarSettings } = props
  const columns = makeColumns(props)
  const Solid = [{ id: "NoData", Html: translations.noData }]
  const columnLayout = makeColumnLayout(columns)

  return {
    Cfg: makeCfg({ id }),
    ...columnLayout,
    Header: makeHeader({ columns, translations }),
    Actions: makeActions(),
    Panel: makePanel({ translations }),
    Toolbar: makeToolbar({
      showAddTaskButton: props.showAddTaskButton,
      showGanttToolbarActions,
      showFilters,
      locale,
      translations,
    }),
    Head: makeHead({ root, showFilters, translations }),
    Def: makeDef({ showGantt, translations }),
    Lang: makeLang({
      firstDayOfWeek: calendarSettings.firstDayOfWeek,
      dateSeparator: calendarSettings.dateFormatSpecs.dateSeparator,
      translations,
    }),
    Solid,
  }
}

const makeActions = () => {
  return {
    OnRightClickCell: "Grid.showContextMenu(Row, Col, Grid)",
    OnClickPanelCopy: "Grid.showContextMenu(Row, Col, Grid)",
    OnIns: "window.Grids.OnAddTask(Grid, Row, Col)",
  }
}

const makeCfg = ({ id = "TaskTreeGrid" } = {}) => {
  const baseCfg = makeCfgBase({ id })
  return {
    ...baseCfg,
    MainCol: "title",
    Dragging: 1, // enables dragging of rows
    UpCounter: 1,
    Version: 7,
  }
}

const makeDef = ({ showGantt, translations }: { showGantt: boolean; translations: TreeGridTranslations }) => {
  return [
    // Default settings for Leaf rows
    {
      Name: "Leaf",
      GanttGanttClass: "Blue",
    },
    // Default settings for Branch rows
    {
      Name: "Branch",
      GanttGanttClass: "Aqua",
      GanttMenu: [],
    },
    //Default settings for all rows
    {
      Name: "R",
      MaxHeight: showGantt ? 16 : 48,
      DefParent: "Branch",
      DefEmpty: "Leaf",
      GanttMenu: [
        {
          Name: translations.deleteMainBar,
        },
      ],
    },
  ]
}

const makeHead = ({ root, showFilters, translations }: MakeHeadProps) => {
  const headerRow = {
    id: "headerRow",
    Name: translations.pageHeader,
    Space: -1,
    Visible: 0,
    PanelVisible: 0,
    CanExport: 0,
    CanPrint: 2,
    Cells: `Logo,GridInfo,PrintDate`,
    LogoRelWidth: 1,
    LogoAligh: "Left",
    LogoVAligh: "middle",
    LogoType: "Html",
    Logo: `<img style="height: 40px;" src="/favicon.png"  alt="company logo"/>`,
    PrintDate: new Date(),
    PrintDateType: "Date",
    PrintDateFormat: root.dateFormat,
    PrintDateCanEdit: 0,
    GridInfo: `<span class="largeText">${root.summary}<span>`,
    GridInfoType: "Html",
    GridInfoRelWidth: 2,
  }
  const filtersRow = {
    CanExport: 0,
    CanPrint: 0,
    id: "filterRow",
    Kind: "Filter",
    ganttBarColorRange: 1,
    ganttBarColorFilterEnumKeys: 1,
    ganttBarColorEnumKeys: makeColorKeysEnum(translations),
    statusFilterOff: translations.statusFilterOffLabel,
    stateFilterOff: translations.stateFilterOffLabel,
    managersFilterOff: translations.responsibleFilterOffLabel,
    participantsFilterOff: translations.participantsFilterOffLabel,
    suppliersFilterOff: translations.suppliersFilterOffLabel,
    workspacesFilterOff: translations.workspacesFilterOffLabel,
    Visible: showFilters ? 1 : 0,
  }

  return [headerRow, filtersRow]
}

const makeHeader = ({ columns, translations }: MakeHeaderProps) => {
  const leftColsWithHeaderAndName = columns.LeftCols.filter((col) => "header" in col && "Name" in col)
  const middleColsWithHeaderAndName = columns.Cols.filter((col) => "header" in col && "Name" in col)
  const rightColsWithHeaderAndName = columns.RightCols.filter((col) => "header" in col && "Name" in col)
  const colsWithHeaderAndName = [
    ...leftColsWithHeaderAndName,
    ...middleColsWithHeaderAndName,
    ...rightColsWithHeaderAndName,
  ] as ColumnWithNameAndHeader[]
  const Header: Record<string, string | number> = {
    Name: translations.rowHeaders,
    PanelType: "Text", // To show the panel column header name as text in the header
    plannedStartDateClass: "text-right",
    plannedEndDateClass: "text-right",
    actualStartDateClass: "text-right",
    actualEndDateClass: "text-right",
    durationClass: "text-center",
    daysLeftClass: "text-center",
    statusClass: "text-center",
    ganttBarColorClass: "text-center",
    Wrap: 1,
  }
  colsWithHeaderAndName.forEach((col) => {
    Header[col.Name] = col.header
  })
  return Header
}

const makeLang = ({ firstDayOfWeek, dateSeparator, translations }: MakeLangProps) => {
  return {
    Format: {
      FirstWeekDay: firstDayOfWeek,
      GMT: 1,
      DateSeparator: dateSeparator,
      StrictDates: 7, // To fix the behavior where the main bar could be dragged into the weekend
      LongDayNames: translations.longDayNames,
      ShortDayNames: translations.shortDayNames,
      Day2CharNames: translations.day2CharNames,
      Day1CharNames: translations.day1CharNames,
      LongMonthNames: translations.longMonthNames,
      LongMonthNames2: translations.longMonthNames,
      ShortMonthNames: translations.shortMonthNames,
    },

    Gantt: {
      DelGanttDep: translations.deleteDependency,
      GanttDepLagChange: translations.changeDependencyLag,
      ErrEnd: translations.startDateAfterEndDateError,
      ErrStart: translations.startDateAfterEndDateError,
    },
    MenuColumns: {
      Caption: translations.chooseItemsLabel,
      ColsCaption: translations.chooseColumnsLabel,
      SpaceCaption: translations.chooseToolbarsLabel,
      RowsCaption: translations.chooseRowsLabel,
    },
    MenuButtons: {
      Ok: translations.okButtonLabel,
      Cancel: translations.cancelButtonLabel,
      HideAll: translations.hideAllButtonLabel,
      All: translations.allOnButtonLabel,
      Clear: translations.clear,
    },
    MenuPrint: {
      Caption: translations.printOptionsLabel,
      PrintOptions: translations.printOptionsLabel,
      PrintRows: translations.printMaxRowsPerPageLabel,
      PrintPageRoot: translations.printStartRowLabel,
      PrintPageSize: translations.printPageSizeLabel,
      PrintPageOptions: translations.printBrowserPrintInfoLabel,
      PrintPageOrientation: translations.printPageOrientationLabel,
      PrintPageOrientations: `${translations.portraitLabel},${translations.landscapeLabel}`,
      PrintMarginWidth: translations.printMarginWidthLabel,
      PrintMarginHeight: translations.printMarginHeightLabel,
      PDFFitPage: translations.printFitToPageLabel,
      PDFFitPages: `${translations.noneLabel},${translations.widthLabel},${translations.heightLabel},${translations.singlePageLabel}`,
      PrintDPI: translations.printDPIRatioLabel,
      PrintWidth: translations.printPageWidthLabel,
      PrintHeight: translations.printPageHeightLabel,
      PrintResults: translations.printSizeInfo,
      PrintExpanded: translations.printExpandedRowsLabel,
      PrintVisible: translations.printVisibleGantLabel,
    },
    MenuExport: {
      Caption: translations.exportOptionsLabel,
      ExportOptions: translations.exportOptionsLabel,
      ExportName: translations.exportFileNameLabel,
      ExportFormat: translations.exportFileFormatLabel,
      ExportExpanded: translations.exportAllRowsExpandedLabel,
      ExportOutline: translations.exportTreeInExcelLabel,
      ExportIndent: translations.exportIndentTreeInMainColumnLabel,
      ExportFixedRows: translations.exportFreezeHeadRowsLabel,
      ExportFixedCols: translations.exportFreezeLeftColumnsLabel,
      ExportEmpty: translations.exportEmptyCellsLabel,
    },
    MenuFilter: {
      F0: translations.filterOffLabel,
      F1: translations.filterEqualLabel,
      F2: translations.filterNotEqualLabel,
      F3: translations.filterLessThanLabel,
      F4: translations.filterLessThanOrEqualLabel,
      F5: translations.filterGreaterThanLabel,
      F6: translations.filterGreaterThanOrEqualLabel,
      F7: translations.filterBeginsWithLabel,
      F8: translations.filterDoesnotBeginWithLabel,
      F9: translations.filterEndsWithLabel,
      F10: translations.filterDoesnotEndWithLabel,
      F11: translations.filterContainsLabel,
      F12: translations.filterDoesnotContainLabel,
    },
  }
}

const makePanel = ({ translations }: { translations: TreeGridTranslations }) => {
  return {
    Move: 0, // hides the drag icon on the panel
    PanelDeleteTip: translations.archiveTask,
    Copy: 1,
    PanelCopyTip: translations.showMenu,
  }
}

const makeToolbar = ({
  showAddTaskButton,
  showGanttToolbarActions,
  showFilters,
  locale,
  translations,
}: MakeToolbarProps) => {
  const addTaskButtonHtml = getAddTaskButtonHtml(translations.addTask)

  /**
   * @link https://www.treegrid.com/Doc/GlobalSettings.htm#DefaultToolbar
   */
  return new ToolbarBuilder()
    .addCustomToolbarConfig({
      Cells05Custom: showAddTaskButton ? "AddTask" : "",
      Cells10Search: "Search",
      AddTaskType: "Html",
      AddTask: addTaskButtonHtml,
      AddTaskOnClick: "window.Grids.OnAddTask(Grid, Row, Col, event)",
      AddTaskCanFocus: 0,
    })
    .addToolbarSearch(translations)
    .addToolbarFilter(showFilters)
    .addToolbarGanttZoom({ showGanttToolbarActions, translations })
    .addToolbarTips(translations)
    .addToolbarZoomTips(translations)
    .build()
}

function getAddTaskButtonHtml(addTaskButtonLabel: string) {
  const html = `
  <div class="toolbarAddTaskButtonRoot">
    <button class="MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary" tabindex="0" type="button">
      <div id="addTaskButtonClickArea"></div>
      <span class="MuiButton-label">
        ${addTaskButtonLabel}
        <span class="MuiButton-endIcon MuiButton-iconSizeMedium">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
              <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
      </span>
      </span>
    </button>
    <input type="number" value="1" min="1" max="100" name="taskToAddCount" onfocus="this.select()" onkeydown="window.Grids.OnKeyDownAddTaskCount(event)"/>
  </div>`
  return html
}

type MakeToolbarProps = {
  showAddTaskButton: boolean
  showGanttToolbarActions: boolean
  showFilters: boolean
  locale: string
  translations: TreeGridTranslations
}

type MakeLangProps = {
  firstDayOfWeek: DayOfWeek
  dateSeparator: string
  translations: TreeGridTranslations
}

type MakeHeadProps = {
  root: RootNode
  showFilters: boolean
  translations: TreeGridTranslations
}

type MakeHeaderProps = {
  columns: MadeColumns
  translations: TreeGridTranslations
}

type MakeLayoutProps = {
  id: string
  root: RootNode
  showActualBar: boolean
  showBaselineBar: boolean
  showGantt: boolean
  showGanttToolbarActions: boolean
  showAddTaskButton: boolean
  showFilters: boolean
  translations: TreeGridTranslations
  visibleColumns?: string[]
  options: Option[]
  locale: string
  defaultZoom: string
  zoomList: GanttZoom[]
  calendarSettings: CalendarSettingsViewModel
}

type Column = Partial<TCol> & {
  header?: string
}

type MadeColumns = {
  LeftCols: Column[]
  Cols: Column[]
  RightCols: Column[]
}

type ColumnWithNameAndHeader = {
  header: string
  Name: string
}
